import * as Yup from "yup";
import { useFormik } from "formik";
import { useEffect, useCallback } from "react";
import { useDataFetch } from "../../hooks/useDataFetch";
import { Config } from "../../../config/config";
import { useNavigate } from "react-router-dom";
import useCookie from "../../hooks/useCookie";
import { useConfig } from "../../store/selectors/useConfig";
import { useAppState } from "../../store/contextProvider";
import { AppActions } from "../../store/reducers";

export const useLoginFormConfig = () => {
  const { callLoginAPI } = useDataFetch();
  const navigate = useNavigate();
  const { setToken, setTradingMode } = useCookie();
  const { updateLoginSuccess } = useConfig();
  const { updateStore } = useAppState();

  const loginSchema = Yup.object().shape({
    userName: Yup.string().required("Username is required"),
    password: Yup.string().required("Password is required"),
    totp: Yup.string().required("TOTP is required").length(6, "TOTP must be 6 characters"),
    virtual: Yup.string().oneOf(["LT", "PT"]).required("Mode is required"),
  });

  const updateTheme = useCallback(
    (mode) => {
      updateStore({
        type: AppActions.updateTheme,
        payload: { mode, isVirtual: mode === "PT" },
      });
    },
    [updateStore]
  );

  const loginForm = useFormik({
    initialValues: {
      userName: "",
      password: "",
      totp: "",
      virtual: "PT",
    },
    validationSchema: loginSchema,
    onSubmit: async (values) => {
      const payload = {
        clientcode: values.userName,
        password: values.password,
        totp: values.totp,
        virtual: values.virtual,
      };
      const loginResponse = await callLoginAPI(Config.ITradeAPI.createSession, payload);

      if (loginResponse.status && loginResponse.data) {
        const data = loginResponse.data;
        data.isVirtual = values.virtual === "PT";
        updateTheme(values.virtual);
        setTradingMode(values.virtual);
        const serverValue = data.userDetails?.server;
        setToken({
          profileID: data.userDetails?.profileID,
          server: serverValue,
          userName: values.userName,
          email: data.userDetails?.email,
        });
        await updateLoginSuccess(true, data.userDetails || data, serverValue);
        navigate("/Dashboard");
      }
    },
  });

  useEffect(() => {
    updateTheme("PT");
  }, [updateTheme]);

  useEffect(() => {
    if (loginForm.values.virtual) updateTheme(loginForm.values.virtual);
  }, [loginForm.values.virtual, updateTheme]);

  const handleThemeChange = (mode) => {
    updateTheme(mode);
    loginForm.setFieldValue("virtual", mode);
  };

  return { loginForm, handleThemeChange };
};
